import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import Glossy3DCard from "@/components/Glossy3DCard";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Server, Plus, UploadCloud, Wifi, Trash2, CheckCircle2, AlertTriangle, ExternalLink, FileJson, Mail, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const SCOPES = [
  "admin.directory.user", "admin.directory.user.security", "admin.directory.user.alias",
  "admin.directory.group", "admin.directory.group.member", "admin.directory.orgunit",
  "admin.directory.device.mobile", "admin.directory.device.mobile.action",
  "admin.directory.domain", "admin.directory.rolemanagement", "drive", "cloud-identity.policies.readonly", "cloud-identity.allowlisteddomains.readonly", "cloud-identity.allowlisteddomains",
].map((s) => `https://www.googleapis.com/auth/${s}`);

export default function WorkspaceAccounts() {
  const { domains, refresh } = useDomains();
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("google");
  const [domain, setDomain] = useState("");
  const [label, setLabel] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const resetForm = () => {
    setDomain(""); setLabel(""); setAdminEmail(""); setAccountEmail(""); setApiKey(""); setApiToken("");
    setFile(null); setPreview(null);
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const text = await f.text();
      const j = JSON.parse(text);
      setPreview({ client_email: j.client_email, project_id: j.project_id, client_id: j.client_id, valid: j.type === "service_account" });
    } catch {
      setPreview({ valid: false });
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (provider === "migadu") {
        if (!domain || !accountEmail || !apiKey) { toast.error("Domain, account email and API key are required"); setBusy(false); return; }
        await api.post("/domains/migadu", { domain, label, account_email: accountEmail, api_key: apiKey });
        toast.success("Migadu domain connected");
      } else if (provider === "hostinger") {
        if (!domain || !apiToken) { toast.error("Domain and API token are required"); setBusy(false); return; }
        await api.post("/domains/hostinger", { domain, label, api_token: apiToken });
        toast.success("Hostinger domain connected");
      } else {
        if (!file || !domain || !adminEmail) { toast.error("Domain, admin email and JSON file are required"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("domain", domain);
        fd.append("admin_email", adminEmail);
        fd.append("label", label);
        fd.append("key_file", file);
        await api.post("/domains", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Workspace connected");
      }
      setOpen(false); resetForm(); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      await api.post(`/domains/${id}/test`);
      toast.success("Connection healthy");
      refresh();
    } catch (e) { toast.error(apiError(e)); refresh(); } finally { setTesting(""); }
  };

  const remove = async (id) => {
    try { await api.delete(`/domains/${id}`); toast.success("Domain removed"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Workspace Accounts" subtitle="Connect and manage 50+ Google Workspace domains" icon={Server}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="glossy-emerald relative rounded-xl" data-testid="add-domain-button">
                <span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />Connect Workspace
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Connect a domain</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => setProvider("google")}
                    className={`rounded-xl border p-2 text-xs font-medium transition ${provider === "google" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white/60 text-slate-500"}`}
                    data-testid="provider-google">
                    <Server className="mx-auto mb-1 h-4 w-4" />Google
                  </button>
                  <button type="button" onClick={() => setProvider("migadu")}
                    className={`rounded-xl border p-2 text-xs font-medium transition ${provider === "migadu" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white/60 text-slate-500"}`}
                    data-testid="provider-migadu">
                    <Mail className="mx-auto mb-1 h-4 w-4" />Migadu
                  </button>
                  <button type="button" onClick={() => setProvider("hostinger")}
                    className={`rounded-xl border p-2 text-xs font-medium transition ${provider === "hostinger" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white/60 text-slate-500"}`}
                    data-testid="provider-hostinger">
                    <Inbox className="mx-auto mb-1 h-4 w-4" />Hostinger
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Domain</Label>
                    <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.com" data-testid="domain-input" /></div>
                  <div className="space-y-1.5"><Label>Label (optional)</Label>
                    <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Acme Corp" data-testid="label-input" /></div>
                </div>

                {provider === "google" && (
                <>
                <div className="space-y-1.5"><Label>Super-admin email (impersonated)</Label>
                  <Input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" data-testid="admin-email-input" /></div>

                <div className="space-y-1.5">
                  <Label>Service-account JSON key</Label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                    className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-6 text-center transition hover:bg-emerald-50"
                    data-testid="json-file-dropzone"
                  >
                    <UploadCloud className="h-8 w-8 text-emerald-500" />
                    <p className="text-sm text-slate-600">{file ? file.name : "Drag & drop or click to upload .json"}</p>
                    <input ref={fileRef} type="file" accept=".json,application/json" className="hidden"
                      onChange={(e) => handleFile(e.target.files[0])} data-testid="json-file-input" />
                  </div>
                </div>

                {preview && (
                  <div className={`rounded-2xl p-3 text-sm ${preview.valid ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid="json-preview">
                    {preview.valid ? (
                      <div className="space-y-1">
                        <p className="flex items-center gap-2 font-medium"><FileJson className="h-4 w-4" />Valid service account</p>
                        <p className="font-mono text-xs">{preview.client_email}</p>
                        <p className="text-xs">Project: {preview.project_id} · Client ID: {preview.client_id}</p>
                      </div>
                    ) : (<p className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Invalid service-account JSON</p>)}
                  </div>
                )}

                <details className="rounded-2xl bg-white/70 p-3 text-xs text-slate-600">
                  <summary className="cursor-pointer font-medium text-slate-700">Setup guide: enable Domain-Wide Delegation</summary>
                  <ol className="ml-4 mt-2 list-decimal space-y-1">
                    <li>In Google Cloud, enable the <b>Admin SDK API</b> and <b>Drive API</b>.</li>
                    <li>Create a Service Account, enable domain-wide delegation, and download the JSON key.</li>
                    <li>In your Admin console → Security → API Controls → Domain-wide delegation, add the service account <b>Client ID</b> with these scopes:</li>
                  </ol>
                  <div className="mt-2 max-h-28 overflow-y-auto rounded-lg bg-slate-900 p-2 font-mono text-[10px] text-emerald-300">
                    {SCOPES.join(",\n")}
                  </div>
                </details>
                </>
                )}
                {provider === "migadu" && (
                <>
                <div className="space-y-1.5"><Label>Migadu account email</Label>
                  <Input value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} placeholder="you@yourlogin.com" data-testid="migadu-account-email-input" /></div>
                <div className="space-y-1.5"><Label>Migadu API key</Label>
                  <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="API key from admin.migadu.com" data-testid="migadu-api-key-input" /></div>
                <div className="rounded-2xl bg-white/70 p-3 text-xs text-slate-600">
                  Create a key at <b>admin.migadu.com → My Account → API Keys</b>. The username is your Migadu account login email; the key is the password. Stored encrypted.
                </div>
                </>
                )}
                {provider === "hostinger" && (
                <>
                <div className="space-y-1.5"><Label>Hostinger API token</Label>
                  <Input type="password" value={apiToken} onChange={(e) => setApiToken(e.target.value)} placeholder="Token from hPanel → API" data-testid="hostinger-api-token-input" /></div>
                <div className="rounded-2xl bg-white/70 p-3 text-xs text-slate-600">
                  Must be a <b>Hostinger Email</b> domain (not Titan). Create a token in hPanel → API. The order ID is detected automatically. Stored encrypted.
                </div>
                </>
                )}
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={busy} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-domain-button">
                  <span className="spec-shine" />{busy ? "Connecting…" : "Save & Connect"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {domains.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center text-slate-500">No workspaces yet. Click "Connect Workspace" to add one.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {domains.map((d) => (
            <Glossy3DCard key={d.id} testid={`workspace-account-card-${d.domain}`} className="p-5">
              <span className="spec-shine" />
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <p className="font-heading text-lg font-bold text-slate-900">{d.label}</p>
                  <p className="text-sm text-slate-500">{d.domain}</p>
                </div>
                <span className={`flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${d.status === "connected" ? "bg-emerald-100 text-emerald-700" : d.status === "error" ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
                  {d.status === "connected" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{d.status}
                </span>
              </div>
              <div className="space-y-1 text-xs text-slate-500">
                <p>Admin: <span className="text-slate-700">{d.admin_email}</span></p>
                {(d.provider === "migadu" || d.provider === "hostinger") ? (
                  <>
                    <p className="inline-block rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-sky-700">{d.provider === "hostinger" ? "Hostinger Email" : "Migadu Email"}</p>
                    <p>Mailboxes: <span className="text-slate-700">{d.mailbox_count ?? "—"}</span></p>
                  </>
                ) : (
                  <>
                    <p className="truncate">SA: <span className="font-mono">{d.service_account_email}</span></p>
                    <p>Users: <span className="text-slate-700">{d.user_count ?? "—"}</span></p>
                  </>
                )}
                {d.last_error && <p className="text-rose-500">⚠ {d.last_error}</p>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 rounded-xl bg-white/60" onClick={() => test(d.id)} disabled={testing === d.id} data-testid={`test-domain-${d.domain}`}>
                  <Wifi className="mr-1 h-3.5 w-3.5" />{testing === d.id ? "Testing…" : "Test"}
                </Button>
                {(d.provider === "migadu" || d.provider === "hostinger") ? (
                  <Link to="/mailboxes">
                    <Button variant="outline" size="sm" className="rounded-xl bg-white/60" data-testid={`manage-mailboxes-${d.domain}`}><Mail className="h-3.5 w-3.5" /></Button>
                  </Link>
                ) : (
                  <a href={`https://admin.google.com/ac/home`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="rounded-xl bg-white/60"><ExternalLink className="h-3.5 w-3.5" /></Button>
                  </a>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl bg-white/60 text-rose-500" data-testid={`delete-domain-${d.domain}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove {d.domain}?</AlertDialogTitle>
                      <AlertDialogDescription>This removes the stored credentials from the portal. Your Google Workspace is not affected.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={() => remove(d.id)}>Remove</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Glossy3DCard>
          ))}
        </div>
      )}
    </div>
  );
}
